
import React, { useState, useEffect } from "react";
import { formatRelativeTime } from "@/lib/utils";
import { motion } from "framer-motion";
import { Mail, User, Calendar, ExternalLink, X, Activity } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { GET_USER } from "@/integrations/apollo/operations";
import { useQuery } from "@apollo/client";

const planBadgeColors: Record<string, string> = {
  basic: "bg-gray-100 text-gray-800 hover:bg-gray-200",
  pro: "bg-blue-100 text-blue-800 hover:bg-blue-200",
  enterprise: "bg-purple-100 text-purple-800 hover:bg-purple-200"
};

export interface User {
  id: string;
  email: string;
  name?: string;
  source?: string;
  signupDate: string;
  totalVideos: number;
  total_videos: number; // Added to match API response
  totalLoggedTime: number;
  lastActive: string;
  plan: "basic" | "pro" | "enterprise";
  role: string;
  created_at: string;
  updated_at: string;
  first_video_created_at: string;
  last_video_created_at: string;
  videos_count_by_website_source: Record<string, number>;
  insta_login_link?: string;
}

interface UserDetailViewProps {
  user: User;
  onClose: () => void;
  onContact: (user: User) => void;
}

const UserDetailView = ({ user, onClose, onContact }: UserDetailViewProps) => {
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const sourceColors: Record<string, string> = {
    airbnb: "#FF4081",
    propertysimple: "#f05a27",
    other: "#95A5A6"
  };
  const sources = { ...user.videos_count_by_website_source };
  const returnedValues = Object.values(sources).reduce((acc, count) => acc + count, 0);
  const { data: userData } = useQuery(GET_USER, {
    variables: { id: user.id },
  });

  if (returnedValues < user.total_videos) {
    sources.other = user.total_videos - returnedValues;
  }

  return (
    <motion.div
      className="bg-card rounded-lg border shadow-sm"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex justify-between items-center p-6 border-b">
        <div className="flex gap-2">
          <h2 className="text-2xl font-bold">{user.name}</h2>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={planBadgeColors[user.plan]}>
              {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
            </Badge>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Contact Information</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span>{user.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>User ID: {user.id}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>Signed up {formatRelativeTime(user.created_at)}</span>
              </div>
            </div>

            <h3 className="text-lg font-medium pt-2">User Activity</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">First video created</span>
                <span className="font-medium">{formatRelativeTime(user.first_video_created_at)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Last video created</span>
                <span className="font-medium">{formatRelativeTime(user.last_video_created_at)}</span>
              </div>
            </div>

            <div className="text-right">
              <Dialog open={isActivityModalOpen} onOpenChange={setIsActivityModalOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex-1">
                    <Activity className="mr-2 h-4 w-4" />
                    View Activity
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[75%]">
                  <DialogHeader>
                    <DialogTitle>User Activity Details</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                      <h4 className="font-medium">User Videos</h4>
                    </div>
                    <div className="relative overflow-x-auto">
                      <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                          <tr>
                            <th scope="col" className="px-6 py-3">
                              ID
                            </th>
                            <th scope="col" className="px-6 py-3">
                              Display Title
                            </th>
                            <th scope="col" className="px-6 py-3">
                              Created At
                            </th>
                            <th scope="col" className="px-6 py-3">
                              Status
                            </th>
                            <th scope="col" className="px-6 py-3">
                              Downloaded
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {userData?.user?.videos?.length > 0 ? (
                            userData.user.videos.map((video) => (
                              <tr key={video.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600">
                                <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                  {video.id}
                                </th>
                                <td className="px-6 py-4">
                                  {video.display_title}
                                </td>
                                <td className="px-6 py-4">
                                  {formatRelativeTime(video.created_at)}
                                </td>
                                <td className="px-6 py-4 capitalize">
                                  {video.status}
                                </td>
                                <td className="px-6 py-4">
                                  {video.downloaded ? 'Yes' : 'No'}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="text-center py-4">
                                No videos found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium">Usage Statistics</h3>

            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-sm font-medium">Total Videos</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="text-4xl font-bold">{user.total_videos}</div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm font-medium">Sources</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="space-y-4">
                  {/* Progress bar showing distribution */}
                  <div className="h-2 w-full bg-muted-foreground rounded-full flex overflow-hidden">
                    {Object.entries(sources).map(([source, count], index) => {
                      const percentage = (count / user.total_videos) * 100;
                      return (
                        <div key={index} style={{ backgroundColor: sourceColors[source], width: `${percentage}%` }} className={cn(
                          'h-2 transition-all duration-300',
                          index === 0 && 'rounded-l-full',
                          index === Object.entries(sources).length - 1 && 'rounded-r-full'
                        )} />
                      );
                    })}
                  </div>

                  {/* Legend with percentages */}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(sources).map(([source, count], index) => {
                      const percentage = (count / user.total_videos) * 100;

                      return (
                        <div key={index} className="flex items-center gap-2">
                          <div className='w-3 h-3 rounded-full' style={{ backgroundColor: sourceColors[source] }} />
                          <span>{source}</span>
                          <span className="ml-auto font-medium">{percentage.toFixed(1)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm font-medium">Activity Level</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="mb-2 flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Based on usage compared to other users</span>
                  <span className="text-sm font-medium">
                    {user.total_videos > 20 ? 'High' : user.total_videos > 10 ? 'Medium' : 'Low'}
                  </span>
                </div>
                <Progress value={user.total_videos > 20 ? 90 : user.total_videos > 10 ? 50 : 20} className="h-2" />
              </CardContent>
            </Card>

            <div className="flex gap-2 pt-4">
              <Button className="flex-1" onClick={() => onContact(user)} disabled>
                <Mail className="mr-2 h-4 w-4" />
                Contact User
              </Button>
            </div>

            {user.insta_login_link && (
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { window.location.href = user.insta_login_link || '#'; }}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Log in as User
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default UserDetailView;
